import React, { useEffect, useMemo, useRef, useState } from "react";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import { Dialog } from "components/main/utils/Dialog";
import { SearchBar } from "components/common/SearchBar";
import { InlineSpinner } from "components/common/InlineSpinner";
import { LoadMoreButton } from "components/common/LoadMoreButton";
import {
  TeamMember,
  addTeamMembers,
  fetchSortedMembersPage,
  removeTeamMember,
  updateMemberAccessLevel,
} from "./TeamsApi";
import { resendInvitation } from "@/lib/api/resendInvitation";
import { TeamMemberRow } from "./TeamMemberRow";
import { showNotificationGlobal } from "stores/useNotificationStore";
import {
  EmailChipInput,
  EmailChip,
} from "components/shareSection/EmailChipInput";
import { ResourceType, TeamEmailEntry } from "components/shareSection/entities";
import { Button } from "components/common/Button";
import {
  SelectDropdown,
  SelectOption,
} from "components/main/utils/SelectDropdown";
import {
  appendUnique,
  useTeamsStore,
} from "@/components/main/stores/teamsStore";

interface TeamMembersDialogProps {
  open: boolean;
  onClose: () => void;
  teamId: string;
  teamName: string;
  isAdmin: boolean;
}

const ROLE_OPTIONS: SelectOption[] = [
  { label: "Admin", value: "Admin" },
  { label: "Member", value: "Member" },
];

export const TeamMembersDialog: React.FC<TeamMembersDialogProps> = ({
  open,
  onClose,
  teamId,
  teamName,
  isAdmin,
}) => {
  // Store-backed; the sidebar reads the same source.
  const baseMembers = useTeamsStore((s) => s.teamMembers[teamId]);
  const baseToken = useTeamsStore((s) => s.teamMembersToken[teamId]);
  const baseLoadingMore = useTeamsStore(
    (s) => s.teamMembersLoadingMore[teamId] ?? false,
  );
  const fetchTeamMembers = useTeamsStore((s) => s.fetchTeamMembers);
  const loadMoreTeamMembers = useTeamsStore((s) => s.loadMoreTeamMembers);
  const removeTeamMemberFromCache = useTeamsStore(
    (s) => s.removeTeamMemberFromCache,
  );
  const updateTeamMemberRole = useTeamsStore((s) => s.updateTeamMemberRole);

  const [chips, setChips] = useState<EmailChip[]>([]);
  const [chipError, setChipError] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState("Member");
  const [isAdding, setIsAdding] = useState(false);

  // Search has its own paginated state, separate from the store.
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<TeamMember[] | null>(null);
  const [searchToken, setSearchToken] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingMoreSearch, setIsLoadingMoreSearch] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [updatingMemberIds, setUpdatingMemberIds] = useState<Set<string>>(
    new Set(),
  );
  const [successMemberIds, setSuccessMemberIds] = useState<Set<string>>(
    new Set(),
  );

  const currentUserEmail = useMemo(
    () => localStorage.getItem("email") || "",
    [],
  );
  const currentUserName = useMemo(
    () => localStorage.getItem("displayName") || currentUserEmail,
    [currentUserEmail],
  );

  useEffect(() => {
    if (!open || !teamId) return;
    void fetchTeamMembers(teamId);
  }, [open, teamId, fetchTeamMembers]);

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setSearchResults(null);
      setSearchToken(null);
      setIsSearching(false);
      setChips([]);
      setChipError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!searchQuery) {
      setSearchResults(null);
      setSearchToken(null);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(() => {
      fetchSortedMembersPage(teamId, { search: searchQuery })
        .then(({ members, continuationToken }) => {
          setSearchResults(members);
          setSearchToken(continuationToken);
        })
        .catch((error: unknown) => {
          const message =
            error instanceof Error ? error.message : "Search failed";
          showNotificationGlobal(message, "error");
          setSearchResults([]);
          setSearchToken(null);
        })
        .finally(() => setIsSearching(false));
    }, 300);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchQuery, open, teamId]);

  const isSearchMode = !!searchQuery;
  const displayMembers = isSearchMode
    ? (searchResults ?? [])
    : (baseMembers ?? []);
  const hasMore = isSearchMode ? !!searchToken : !!baseToken;
  const isLoadingMore = isSearchMode ? isLoadingMoreSearch : baseLoadingMore;
  const isInitialLoading = isSearchMode
    ? isSearching && searchResults === null
    : baseMembers === undefined;

  const handleLoadMore = async () => {
    if (isSearchMode) {
      if (!searchToken) return;
      setIsLoadingMoreSearch(true);
      try {
        const { members, continuationToken } = await fetchSortedMembersPage(
          teamId,
          { search: searchQuery, continuationToken: searchToken },
        );
        setSearchResults((prev) =>
          appendUnique(prev ?? [], members, (m) => m.user.user_id_js()),
        );
        setSearchToken(continuationToken);
      } finally {
        setIsLoadingMoreSearch(false);
      }
    } else {
      void loadMoreTeamMembers(teamId);
    }
  };

  const handleAddMembers = async () => {
    const validEmails = chips
      .filter((chip) => chip.status === "valid")
      .map((chip) => chip.email);

    if (chips.length === 0) {
      setChipError("Add at least one email to invite.");
      return;
    }
    if (validEmails.length === 0) {
      setChipError("Resolve the highlighted emails before adding.");
      return;
    }

    setIsAdding(true);
    try {
      const members: TeamEmailEntry[] = validEmails.map((email) => ({
        email,
        accessLevel: selectedRole,
      }));
      const { atCapacityEmails } = await addTeamMembers(
        teamId,
        JSON.stringify(members),
        teamName,
      );
      setChips([]);
      setChipError(null);
      if (atCapacityEmails.length > 0) {
        showNotificationGlobal(
          `We've hit our account limit, so these weren't invited: ${atCapacityEmails.join(", ")}`,
          "error",
        );
      } else {
        showNotificationGlobal("Members added", "success");
      }
      await fetchTeamMembers(teamId, true, true);
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed to add members";
      showNotificationGlobal(message, "error");
    } finally {
      setIsAdding(false);
    }
  };

  const handleUpdateAccess = async (member: TeamMember, newAccess: string) => {
    const userId = member.user.user_id_js();
    setUpdatingMemberIds((prev) => new Set(prev).add(userId));
    try {
      await updateMemberAccessLevel(
        teamId,
        userId,
        newAccess,
        teamName,
        member.user.user_name_js() || "",
        member.user.user_email_js(),
      );
      updateTeamMemberRole(teamId, userId, newAccess);
      setSuccessMemberIds((prev) => new Set(prev).add(userId));
      showNotificationGlobal("Member access updated", "success");
      setTimeout(() => {
        setSuccessMemberIds((prev) => {
          const next = new Set(prev);
          next.delete(userId);
          return next;
        });
      }, 2000);
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed to update access";
      showNotificationGlobal(message, "error");
    } finally {
      setUpdatingMemberIds((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  };

  const handleRemoveMember = async (member: TeamMember) => {
    const userId = member.user.user_id_js();
    const userEmail = member.user.user_email_js();
    setUpdatingMemberIds((prev) => new Set(prev).add(userId));
    try {
      await removeTeamMember(
        teamId,
        userId,
        teamName,
        member.user.user_name_js() || "",
        userEmail,
      );
      removeTeamMemberFromCache(teamId, userId);
      if (isSearchMode) {
        setSearchResults(
          (prev) => prev?.filter((m) => m.user.user_id_js() !== userId) ?? null,
        );
      }
      showNotificationGlobal(
        member.isPending ? "Invitation cancelled" : "Member removed",
        "success",
      );
      void fetchTeamMembers(teamId, true, true);
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed to remove member";
      showNotificationGlobal(message, "error");
    } finally {
      setUpdatingMemberIds((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  };

  const handleResendInvitation = async (email: string) => {
    await resendInvitation("team", teamId, teamName, email, currentUserName);
    showNotificationGlobal("Invitation resent", "success");
  };

  const searchBarShown = displayMembers.length > 0 || isSearchMode;

  return (
    <Dialog
      open={open}
      title="Manage Members"
      onClose={onClose}
      className="w-150! max-w-[90vw] overflow-visible"
      autoSpacing={false}
    >
      <div className="flex flex-col w-full overflow-visible space-y-4">
        {isAdmin && (
          <div className="flex flex-col gap-2">
            <EmailChipInput
              chips={chips}
              onChipsChange={setChips}
              entityId={teamId}
              resourceType={ResourceType.Team}
              currentUserEmail={currentUserEmail}
              placeholder="Add members by email (comma-separated)"
              error={chipError}
              onErrorChange={setChipError}
            />
            <div className="flex items-center gap-2 justify-end">
              {chipError && (
                <span
                  className="text-error text-sm mr-auto truncate"
                  role="alert"
                >
                  {chipError}
                </span>
              )}
              <SelectDropdown
                options={ROLE_OPTIONS}
                value={selectedRole}
                onChange={(value) => setSelectedRole(String(value))}
                className="w-30! [&_span]:whitespace-nowrap!"
                menuWidthClass="w-[118px]"
                size="md"
                centerOptions={true}
              />
              <Button
                onClick={handleAddMembers}
                disabled={
                  isAdding || chips.every((chip) => chip.status !== "valid")
                }
                loading={isAdding}
                variant="primary"
                size="sm"
                className="h-8! px-6!"
              >
                Add
              </Button>
            </div>
          </div>
        )}

        <div>
          <div className="flex items-center gap-1 h-8 mb-2 cursor-default">
            <PeopleOutlinedIcon className="text-fg-muted-medium icon-sm" />
            <p className="text-fg-muted-semibold">
              Team members
              {displayMembers.length === 0 && !isSearchMode ? " (0)" : ""}
            </p>
          </div>
          {searchBarShown && (
            <div className="mb-2">
              <SearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search members"
                onClose={() => setSearchQuery("")}
                actionTooltip="Clear Search"
              />
            </div>
          )}
          <div className="relative max-h-52 overflow-y-auto">
            {displayMembers.length === 0 ? (
              isInitialLoading || isSearching ? (
                <div className="flex justify-center py-4">
                  <InlineSpinner size={20} />
                </div>
              ) : isSearchMode ? (
                <p className="text-fg-muted-medium text-sm py-2">
                  No members found
                </p>
              ) : null
            ) : (
              <>
                <div className="space-y-1 cursor-default overflow-visible">
                  {displayMembers.map((member) => (
                    <TeamMemberRow
                      key={member.user.user_id_js()}
                      member={member}
                      mode={{
                        kind: "interactive",
                        isAdmin,
                        isUpdating: updatingMemberIds.has(
                          member.user.user_id_js(),
                        ),
                        showSuccess: successMemberIds.has(
                          member.user.user_id_js(),
                        ),
                        onUpdateAccess: handleUpdateAccess,
                        onRemoveMember: handleRemoveMember,
                        onResendInvitation: handleResendInvitation,
                      }}
                    />
                  ))}
                </div>
                {hasMore && (
                  <LoadMoreButton
                    onClick={handleLoadMore}
                    isLoading={isLoadingMore}
                  />
                )}
              </>
            )}
            {isSearching && displayMembers.length > 0 && (
              <div className="absolute inset-0 bg-bg/60 rounded pointer-events-none" />
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};
